/**
 * ScriptedFieldPrompt renderer.
 * Reads action.params.prompt_script (may reference {captured.data.path}),
 * substitutes tokens from capturedData, and shows a chat-bubble prompt +
 * an input matching expected_type.
 *
 * Iter303ag-fix4 — the "💬 Say to caller" prompt is now editable inline
 * (pencil icon) using the same PATCH-template pattern that
 * PropertyIntakeForm's field prompts use. Requires the runner to pass
 * `statusKey` / `taskId` and `onTemplateChanged` for the pencil to render.
 */
import React, { useMemo, useState, useEffect } from 'react';
import { InlineParamEdit } from '../intake/scriptEditControls';

function substituteTokens(template, capturedData) {
  if (!template || typeof template !== 'string') return template || '';
  return template.replace(/\{([^}]+)\}/g, (_, key) => {
    const val = capturedData[key.trim()];
    return val != null ? String(val) : `{${key}}`;
  });
}

export default function ScriptedFieldPrompt({ action, primitive, capturedData, value, onChange, onNext, onBack, canBack,
                                                statusKey, taskId, onTemplateChanged }) {
  const params = action.params || {};
  const promptText = useMemo(
    () => substituteTokens(params.prompt_script, capturedData || {}),
    [params.prompt_script, capturedData]
  );
  const options = (params.options || '').toString().split(',').map(s => s.trim()).filter(Boolean);
  const expected = params.expected_type || 'string';
  const [local, setLocal] = useState(value == null ? '' : value);

  useEffect(() => { setLocal(value == null ? '' : value); }, [action.id, value]);

  const handleChange = (v) => { setLocal(v); onChange(v); };

  const canNext = String(local ?? '').trim().length > 0 || expected === 'confirm_or_adjust';

  const editCtx = (statusKey && taskId && action?.id) ? {
    statusKey, taskId, actionId: action.id, onTemplateChanged,
  } : null;

  return (
    <div>
      <div className="bg-gradient-to-br from-sky-50 to-white border-l-4 border-sky-400 rounded-r-lg p-6 mb-6">
        <div className="text-[10px] uppercase tracking-widest text-sky-600 font-semibold mb-2">💬 Say to caller</div>
        <div className="text-lg text-slate-800 leading-relaxed" data-testid="wf-prompt-text">
          <InlineParamEdit
            editCtx={editCtx}
            paramKey="prompt_script"
            value={params.prompt_script || ''}
            placeholder="(no prompt script set)"
            multiline={true}
            className="text-lg text-slate-800 leading-relaxed"
            dataTestidPrefix="wf-prompt-edit"
          />
          {!editCtx && (promptText || '(no prompt script set)')}
        </div>
      </div>

      <div className="mb-6">
        {options.length > 0 ? (
          <select value={local} onChange={e => handleChange(e.target.value)}
            data-testid="wf-prompt-select"
            className="w-full text-base px-4 py-3 border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-sky-300">
            <option value="">— choose —</option>
            {options.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        ) : (
          <input value={local} onChange={e => handleChange(e.target.value)}
            data-testid="wf-prompt-input"
            placeholder={params.field ? `Enter ${params.field}` : 'Type answer…'}
            autoFocus
            onKeyDown={e => { if (e.key === 'Enter' && canNext) onNext(); }}
            className="w-full text-base px-4 py-3 border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-sky-300" />
        )}
        {params.write_to && (
          <div className="text-xs text-slate-400 mt-2">
            <span className="font-semibold text-slate-500">Writes to:</span> <code className="text-slate-600">{params.write_to}</code>
          </div>
        )}
      </div>

      <div className="flex items-center gap-3">
        {canBack && <button onClick={onBack} data-testid="wf-back-btn"
          className="px-4 py-2 text-slate-500 hover:text-slate-700">← Back</button>}
        <span className="flex-1" />
        <button onClick={onNext} disabled={!canNext}
          data-testid="wf-next-btn"
          className="px-6 py-2 bg-sky-600 text-white font-semibold rounded hover:bg-sky-700 disabled:opacity-40">
          Next →
        </button>
      </div>
    </div>
  );
}
